<template>
  <div role="form">
    <div class="form-group flex flex-col">
      <div v-if="activityTypes.length > 0" class="m-b-sm">
        <label class="block">Contact type <span class="text-danger">*</span></label>
        <select v-model="selectedActivityType" class="form-control">
          <option value="">Select type...</option>
          <option
            v-for="at in activityTypes"
            :key="at.value"
            :value="at.value"
          >
            {{ at.label }}
          </option>
        </select>
      </div>
      <label>{{ noteLabel }}</label>
      <input :id="hiddenInputId" type="hidden" name="content">
      <trix-editor :id="trixDomId" :input="hiddenInputId" class="trix-content"></trix-editor>
      <slot name="after-content"></slot>
      <div v-if="!forAppEmbed" class="flex justify-space-between m-t-sm">
        <button class="btn btn-default" @click.prevent="cancelNote()">
          Cancel
        </button>
        <div>
          <ladda-button @lbClicked="saveNote" el-class="btn-primary" :loading="loading">
            Save
          </ladda-button>
          <div v-if="error">
            <small>
              <span class="text-danger">{{ errorMessage }}</span>
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Tribute from 'tributejs';
import { errorableMix } from './mixins/errorable_mix';
import { trixFileAttachmentMix } from './mixins/trix_file_attachment_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import LaddaButton from '../../shared/ladda_button.vue';

export default {
  name: 'note-form',
  components: {
    LaddaButton,
  },
  mixins: [trixFileAttachmentMix, errorableMix, appEmbedMix],
  props: {
    // URL to POST new notes to
    createUrl: {
      type: String,
      required: true,
    },
    existingNoteContent: {
      type: String,
      default: null,
    },
    existingNoteId: {
      type: Number,
      default: null,
    },
    // For @ mentions
    suggestUrl: {
      type: String,
      default: null,
    },
    // When non-empty, the form requires the user to pick an activity type
    // (e.g. "phone_call", "sms") before saving. Used for "Log Contact"-style
    // flows on prospects. Each entry: { value: String, label: String }
    activityTypes: {
      type: Array,
      default: () => [],
    },
    // Extra top-level params merged into the POST body on create (alongside
    // `note: {...}`). Used by callers that need to piggyback on the note
    // submit — e.g. prospect follow-up resolution sends `clear_follow_up_for`.
    extraParams: {
      type: Object,
      default: () => ({}),
    },
    // In an app embed the native nav bar owns Save, and a successful save
    // pops back to the app with this notification ({ name, data }).
    appEmbedPopNotification: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      content: '',
      trixEditor: null,
      selectedActivityType: '',
    };
  },
  mounted() {
    document.addEventListener('trix-initialize', this.trixInitHandler);
    this.setupNativeSaveButton(this.saveNote);
  },
  destroyed() {
    document.removeEventListener('trix-initialize', this.trixInitHandler);
  },
  computed: {
    trixDomId() {
      const noteId = this.existingNoteId ?? 'new';
      return `note-form-${noteId}`;
    },
    hiddenInputId() {
      return `${this.trixDomId}-hidden`;
    },
    noteLabel() {
      return this.activityTypes.length > 0 ? 'Details' : 'Add note';
    },
  },
  methods: {
    trixInitHandler(event) {
      const vm = this;
      if (event.target.id !== vm.trixDomId) return;
      vm.trixEditor = event.target.editor;

      if (vm.existingNoteContent) {
        vm.content = vm.existingNoteContent;
        vm.trixEditor.loadHTML(vm.content);
      }

      // Load up @ mention functionality
      if (vm.suggestUrl) {
        vm.$apiService.loadAllPages(vm.suggestUrl, 'coaches')
          .then((coaches) => {
            const data = _.map(coaches, (coach) => ({
              key: coach.full_name,
              value: coach.full_name,
            }));
            const tribute = new Tribute({
              values: data,
              allowSpaces: true,
              noMatchTemplate() {
                return '<span style:"visibility: hidden;"></span>';
              },
            });
            const trixDomElement = document.getElementById(vm.trixDomId);
            tribute.attach(trixDomElement);
          })
          .catch((error) => {
            console.log(error);
            console.log('failed to load coaches for mentions');
          });
      }
    },
    // In an app embed the native Save button is waiting on a result too.
    showSaveError(message) {
      this.showError(message);
      this.notifyAppOfValidationError(message);
    },
    cancelNote() {
      this.$emit('cancel-note-form');
    },
    getPlainNoteText() {
      const vm = this;
      if (vm.trixEditor) {
        return vm.trixEditor.getDocument().toString();
      }
      return '';
    },
    saveNote: _.throttle(function () {
      const vm = this;

      if (vm.loading === true) {
        return;
      }
      vm.clearError();

      const content = document.getElementById(vm.hiddenInputId).value;
      if (content.length === 0) {
        vm.showSaveError('Content must exist to save the note.');
        return;
      }

      if (vm.activityTypes.length > 0 && !vm.selectedActivityType) {
        vm.showSaveError('Please select a contact type.');
        return;
      }

      const plainContent = vm.getPlainNoteText();

      vm.loading = true;
      const params = {
        note: {
          content,
          plain_content: plainContent,
        },
      };

      if (vm.activityTypes.length > 0 && vm.selectedActivityType) {
        params.note.activity_type = vm.selectedActivityType;
      }

      if (vm.existingNoteId != null) {
        // PUT
        const url = vm.$apiService.updateNoteUrl(vm.existingNoteId);
        axios.put(url, params)
          .then(() => {
            vm.loading = false;
            vm.$notificationManager.emit('show-toast', 'Note saved', true);
            vm.$emit('note-updated', vm.existingNoteId, content);
            vm.$notificationManager.emit('mentionable-created', 'Note');
            vm.askAppToPop(vm.appEmbedPopNotification);
          })
          .catch(() => {
            vm.loading = false;
            vm.showSaveError('Error updating note');
          });
      } else {
        // POST
        Object.assign(params, vm.extraParams || {});
        axios.post(vm.createUrl, params)
          .then((response) => {
            vm.loading = false;
            vm.$notificationManager.emit('show-toast', 'Note saved', true);
            vm.$emit('note-created', response.data.note);
            vm.$notificationManager.emit('mentionable-created', 'Note');
            vm.askAppToPop(vm.appEmbedPopNotification);
          })
          .catch(() => {
            vm.loading = false;
            vm.showSaveError('Error creating note');
          });
      }
    }, 500),
  },
};
</script>
